import { useState } from 'react';
import { Box, Collapse, Group, Text, UnstyledButton } from '@mantine/core';
import { ChevronDown } from 'lucide-react';
import type { ReactElement, ReactNode } from 'react';
import type { VizAccent } from '@/design/VizAccents';

interface InsightsSectionProps {
    title: string;
    /** Compact overview shown in the header while the section is collapsed. */
    summary: string;
    caption?: string;
    /** @deprecated Section chrome is brand now; the accent is ignored. Kept so callers need not change. */
    accent?: VizAccent;
    defaultOpen?: boolean;
    children: ReactNode;
}

/** Collapsible scorecard module: accent-dotted title, a summary while collapsed, full content when expanded. */
export default function InsightsSection({ title, summary, caption, defaultOpen = false, children }: InsightsSectionProps): ReactElement {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <Box style={{ border: '1px solid var(--bm-color-border)', borderRadius: 'var(--bm-radius)', background: 'var(--bm-color-card)' }}>
            <UnstyledButton onClick={() => setOpen((o) => !o)} w="100%" px="md" py="sm" aria-expanded={open}>
                <Group justify="space-between" wrap="nowrap" gap="md">
                    <Group gap="sm" wrap="nowrap" style={{ flexShrink: 0 }}>
                        <Box w={10} h={10} style={{ background: 'var(--bm-color-brand-accent)', borderRadius: 3, flexShrink: 0 }} />
                        <Text fw={700} style={{ fontSize: '15px', letterSpacing: '-0.01em', whiteSpace: 'nowrap' }}>
                            {title}
                        </Text>
                    </Group>
                    <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
                        {!open && (
                            <Text size="sm" c="dimmed" truncate>
                                {summary}
                            </Text>
                        )}
                        <ChevronDown
                            size={16}
                            style={{
                                transform: open ? 'rotate(180deg)' : undefined,
                                transition: 'transform 150ms ease',
                                color: 'var(--bm-color-text-dimmed)',
                                flexShrink: 0,
                            }}
                        />
                    </Group>
                </Group>
            </UnstyledButton>
            <Collapse in={open}>
                <Box px="md" pb="md">
                    {caption !== undefined && (
                        <Text size="sm" c="dimmed" mb="sm">
                            {caption}
                        </Text>
                    )}
                    {children}
                </Box>
            </Collapse>
        </Box>
    );
}
